/**
 * Verify Email Page
 * Handles email verification action from Firebase Auth email links
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, AlertCircle } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { auth } from '../config/firebaseInit';
import { verifyEmailToken } from '../services/WebCloudFunctionsService';

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleVerification = async () => {
      try {
        // Get the token from the URL (new custom verification flow)
        const token = searchParams.get('token');

        // Legacy Firebase action code support (backward compatibility)
        const actionCode = searchParams.get('oobCode');
        const mode = searchParams.get('mode');

        // Use new token-based verification if token is present
        if (token) {
          const result = await verifyEmailToken(token);

          if (result.success) {
            // Reload the user to get updated emailVerified status
            const currentUser = auth.currentUser;
            if (currentUser) {
              await currentUser.reload();
            }

            setStatus('success');
            setMessage('Your email has been verified successfully! Your account is now active.');

            // Redirect to home after a short delay
            setTimeout(() => {
              navigate('/home', { replace: true });
            }, 3000);
          } else {
            setStatus('error');
            setMessage('Verification failed. Please request a new verification email.');
          }
        }
        // Legacy Firebase action code handling (for backward compatibility)
        else if (actionCode && mode === 'verifyEmail') {
          // This is for old Firebase verification links - we can't handle this without Firebase's applyActionCode
          // But since we're moving to custom emails, this should rarely be needed
          setStatus('error');
          setMessage('This verification link format is no longer supported. Please request a new verification email.');
        }
        else {
          setStatus('error');
          setMessage('Invalid verification link. Please request a new verification email.');
        }
      } catch (error: any) {
        console.error('Email verification error:', error);
        setStatus('error');

        if (error.code === 'deadline-exceeded' || error.message?.includes('expired')) {
          setMessage('This verification link has expired. Please request a new verification email.');
        } else if (error.code === 'not-found' || error.message?.includes('Invalid')) {
          setMessage('This verification link is invalid or has already been used.');
        } else if (error.code === 'auth/expired-action-code') {
          setMessage('This verification link has expired. Please request a new verification email.');
        } else if (error.code === 'auth/invalid-action-code') {
          setMessage('This verification link is invalid or has already been used.');
        } else {
          setMessage(error.message || 'An error occurred during email verification. Please try again.');
        }
      }
    };

    handleVerification();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8">
        <div className="text-center">
          {status === 'loading' && (
            <>
              <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                <Loader className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Verifying Your Email
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while we verify your email address...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Email Verified!
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {message}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Redirecting you to the app...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Verification Failed
              </h1>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-900 dark:text-red-200 text-left">
                    {message}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/admin/login')}
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors duration-200"
              >
                Go to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;


